<script setup>
import { ref, onMounted } from 'vue';

const loading = ref(false);
const error = ref(null);
const successMessage = ref(null);

// Wartungsseiten-Einstellungen
const maintenanceSettings = ref({
  title: 'Website wird gewartet',
  message: 'Wir arbeiten derzeit an Verbesserungen unserer Website. Bitte schauen Sie später wieder vorbei.',
  show_countdown: false,
  end_date: null,
  background_image: null,
  custom_content: null,
  social_links: false,
  show_contact: false
});

// Vorschau des Hintergrundbilds
const backgroundImagePreview = ref('');
const selectedBackgroundImage = ref(null);

// Datum-Zeit-Picker Format
const getDateTimeLocal = (datetime) => {
  if (!datetime) return '';
  
  const date = new Date(datetime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Einstellungen laden
const loadSettings = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    const response = await fetch('/api/maintenance_settings.php');
    const data = await response.json();
    
    if (data.success && data.settings) {
      maintenanceSettings.value = data.settings;
      
      // Konvertieren des Datums für den Datepicker
      if (maintenanceSettings.value.end_date) {
        maintenanceSettings.value.end_date = getDateTimeLocal(maintenanceSettings.value.end_date);
      }
      
      // Bild-Vorschau setzen, wenn vorhanden
      if (maintenanceSettings.value.background_image) {
        backgroundImagePreview.value = maintenanceSettings.value.background_image;
      }
    }
  } catch (err) {
    console.error('Fehler beim Laden der Wartungseinstellungen:', err);
    error.value = 'Die Einstellungen konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
};

// Einstellungen speichern
const saveSettings = async () => {
  try {
    loading.value = true;
    error.value = null;
    successMessage.value = null;
    
    // Bereite die zu sendenden Daten vor
    const settingsToSave = {
      title: maintenanceSettings.value.title,
      message: maintenanceSettings.value.message,
      show_countdown: maintenanceSettings.value.show_countdown,
      end_date: maintenanceSettings.value.end_date,
      custom_content: maintenanceSettings.value.custom_content,
      social_links: maintenanceSettings.value.social_links,
      show_contact: maintenanceSettings.value.show_contact
    };
    
    // Wenn ein neues Hintergrundbild ausgewählt wurde, füge es hinzu
    if (selectedBackgroundImage.value) {
      settingsToSave.background_image = backgroundImagePreview.value;
    }
    
    const response = await fetch('/api/maintenance_settings.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settingsToSave)
    });
    
    const data = await response.json();
    
    if (data.success) {
      successMessage.value = 'Wartungsseite-Einstellungen wurden erfolgreich gespeichert.';
      
      // Zurücksetzen des Bild-Uploads
      selectedBackgroundImage.value = null;
      
      setTimeout(() => {
        successMessage.value = null;
      }, 3000);
    } else {
      throw new Error(data.error || 'Ein unbekannter Fehler ist aufgetreten');
    }
  } catch (err) {
    console.error('Fehler beim Speichern der Einstellungen:', err);
    error.value = `Die Einstellungen konnten nicht gespeichert werden: ${err.message}`;
  } finally {
    loading.value = false;
  }
};

// Bild-Upload-Handler
const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    selectedBackgroundImage.value = file;
    
    // Bild in Base64 konvertieren für Vorschau und Upload
    const reader = new FileReader();
    reader.onload = (e) => {
      backgroundImagePreview.value = e.target.result;
    };
    reader.readAsDataURL(file);
  }
};

// Hintergrundbild entfernen
const removeBackgroundImage = () => {
  maintenanceSettings.value.background_image = null;
  backgroundImagePreview.value = '';
  selectedBackgroundImage.value = null;
  
  // Leere auch das Datei-Input-Feld
  const fileInput = document.getElementById('background-image');
  if (fileInput) {
    fileInput.value = '';
  }
};

// Beim Aufbau der Komponente die Einstellungen laden
onMounted(() => {
  loadSettings();
});
</script>

<template>
  <div class="admin-section maintenance-manager">
    <div class="section-header">
      <h2>Wartungsseite-Einstellungen</h2>
      <p class="section-description">
        Passen Sie an, was Besucher während des Wartungsmodus sehen.
      </p>
    </div>
    
    <div v-if="loading" class="loading-indicator">
      Lade Einstellungen...
    </div>
    
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    
    <div v-if="successMessage" class="success-message">
      {{ successMessage }}
    </div>
    
    <form @submit.prevent="saveSettings" class="settings-form">
      <!-- Grundeinstellungen -->
      <div class="form-section">
        <h3>Grundeinstellungen</h3>
        
        <div class="form-group">
          <label for="title">Titel</label>
          <input
            type="text"
            id="title"
            v-model="maintenanceSettings.title"
            class="form-control"
            placeholder="z.B. Website wird gewartet"
          />
        </div>
        
        <div class="form-group">
          <label for="message">Nachricht</label>
          <textarea
            id="message"
            v-model="maintenanceSettings.message"
            class="form-control"
            rows="3"
            placeholder="Beschreiben Sie, warum die Seite im Wartungsmodus ist"
          ></textarea>
        </div>
        
        <div class="form-group">
          <div class="form-toggle">
            <label for="show-countdown">
              <span>Countdown anzeigen</span>
              <div class="toggle-description">
                Zeigt einen Countdown an, der angibt, wann die Wartung voraussichtlich abgeschlossen ist.
              </div>
            </label>
            <div class="toggle-switch">
              <input
                type="checkbox"
                id="show-countdown"
                v-model="maintenanceSettings.show_countdown"
              />
              <label for="show-countdown" class="toggle-label"></label>
            </div>
          </div>
        </div>
        
        <div class="form-group" v-if="maintenanceSettings.show_countdown">
          <label for="end-date">Ende der Wartung</label>
          <input
            type="datetime-local"
            id="end-date"
            v-model="maintenanceSettings.end_date"
            class="form-control"
          />
          <small>Legen Sie fest, wann die Wartung voraussichtlich abgeschlossen sein wird.</small>
        </div>
      </div>
      
      <!-- Aussehen -->
      <div class="form-section">
        <h3>Aussehen</h3>
        
        <div class="form-group">
          <label for="background-image">Hintergrundbild</label>
          <div class="file-upload-wrapper">
            <label class="file-upload-label">
              <span>{{ selectedBackgroundImage ? selectedBackgroundImage.name : 'Bild auswählen...' }}</span>
              <input
                type="file"
                id="background-image"
                @change="handleImageUpload"
                accept="image/*"
                class="file-upload-input"
              />
            </label>
            <button 
              type="button" 
              class="btn-remove" 
              v-if="backgroundImagePreview" 
              @click="removeBackgroundImage"
            >
              Entfernen
            </button>
          </div>
          
          <div class="image-preview" v-if="backgroundImagePreview">
            <img :src="backgroundImagePreview" alt="Vorschau" />
          </div>
        </div>
        
        <div class="form-group">
          <label for="custom-content">Benutzerdefinierter HTML-Inhalt (optional)</label>
          <textarea
            id="custom-content"
            v-model="maintenanceSettings.custom_content"
            class="form-control code-editor"
            rows="6"
            placeholder="<div class='custom-section'>...</div>"
          ></textarea>
          <small>Sie können benutzerdefiniertes HTML einfügen, um die Wartungsseite nach Ihren Wünschen zu gestalten. Lassen Sie dieses Feld leer, um die Standard-Wartungsseite zu verwenden.</small>
        </div>
      </div>
      
      <!-- Zusätzliche Elemente -->
      <div class="form-section">
        <h3>Zusätzliche Elemente</h3>
        
        <div class="form-group">
          <div class="form-toggle">
            <label for="social-links">
              <span>Social-Media-Links anzeigen</span>
              <div class="toggle-description">
                Zeigt die konfigurierten Social-Media-Links auf der Wartungsseite an.
              </div>
            </label>
            <div class="toggle-switch">
              <input
                type="checkbox"
                id="social-links"
                v-model="maintenanceSettings.social_links"
              />
              <label for="social-links" class="toggle-label"></label>
            </div>
          </div>
        </div>
        
        <div class="form-group">
          <div class="form-toggle">
            <label for="show-contact">
              <span>Kontaktinformationen anzeigen</span>
              <div class="toggle-description">
                Zeigt die konfigurierten Kontaktinformationen auf der Wartungsseite an.
              </div>
            </label>
            <div class="toggle-switch">
              <input
                type="checkbox"
                id="show-contact"
                v-model="maintenanceSettings.show_contact"
              />
              <label for="show-contact" class="toggle-label"></label>
            </div>
          </div>
        </div>
      </div>
      
      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="loading">
          {{ loading ? 'Speichern...' : 'Einstellungen speichern' }}
        </button>
      </div>
    </form>
    
    <!-- Vorschau der Wartungsseite -->
    <div class="maintenance-preview">
      <h3>Vorschau</h3>
      <div class="preview-container">
        <div class="preview-label">Vorschau des aktuellen Designs (vereinfacht)</div>
        <div class="preview-iframe">
          <div class="preview-content">
            <h2>{{ maintenanceSettings.title }}</h2>
            <p>{{ maintenanceSettings.message }}</p>
            
            <div v-if="maintenanceSettings.show_countdown" class="countdown-preview">
              <div class="countdown-item">
                <span class="countdown-number">00</span>
                <span class="countdown-label">Tage</span>
              </div>
              <div class="countdown-item">
                <span class="countdown-number">00</span>
                <span class="countdown-label">Stunden</span>
              </div>
              <div class="countdown-item">
                <span class="countdown-number">00</span>
                <span class="countdown-label">Minuten</span>
              </div>
              <div class="countdown-item">
                <span class="countdown-number">00</span>
                <span class="countdown-label">Sekunden</span>
              </div>
            </div>
            
            <div v-if="maintenanceSettings.social_links" class="social-preview">
              <div class="social-icons">
                <span class="social-icon-preview">Facebook</span>
                <span class="social-icon-preview">YouTube</span>
                <span class="social-icon-preview">TikTok</span>
              </div>
            </div>
            
            <div v-if="maintenanceSettings.show_contact" class="contact-preview">
              <div class="contact-item">
                <span class="contact-label">E-Mail:</span>
                <span class="contact-value">kontakt@example.com</span>
              </div>
              <div class="contact-item">
                <span class="contact-label">Telefon:</span>
                <span class="contact-value">+49 123 456 7890</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.maintenance-manager {
  max-width: 1000px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 2rem;
}

.section-description {
  color: var(--text-muted);
  font-size: 1rem;
  margin-top: 0.5rem;
}

.form-section {
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.form-section h3 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  font-size: 1.25rem;
  color: var(--primary-color);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-control:focus {
  border-color: var(--primary-color);
  outline: none;
}

textarea.form-control {
  resize: vertical;
}

.code-editor {
  font-family: monospace;
  background-color: #f8f9fa;
}

.form-toggle {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.toggle-description {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.toggle-switch {
  position: relative;
  width: 60px;
  height: 28px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 34px;
}

.toggle-label:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .toggle-label {
  background-color: var(--primary-color);
}

input:checked + .toggle-label:before {
  transform: translateX(32px);
}

.file-upload-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.file-upload-label {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: var(--bg-alt);
  color: var(--text-color);
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid var(--border-color);
  transition: background-color 0.3s ease;
}

.file-upload-label:hover {
  background-color: var(--border-color);
}

.file-upload-input {
  display: none;
}

.btn-remove {
  padding: 0.75rem 1.5rem;
  background-color: #f8d7da;
  color: #842029;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.btn-remove:hover {
  background-color: #f5c2c7;
}

.image-preview {
  margin-top: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.5rem;
  max-width: 300px;
}

.image-preview img {
  width: 100%;
  height: auto;
  border-radius: 2px;
}

small {
  display: block;
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
}

.form-actions {
  margin-top: 2rem;
  text-align: right;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border: none;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: var(--primary-dark);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-indicator {
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
}

.error-message,
.success-message {
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.error-message {
  background-color: #f8d7da;
  color: #842029;
  border: 1px solid #f5c2c7;
}

.success-message {
  background-color: #d1e7dd;
  color: #0f5132;
  border: 1px solid #badbcc;
}

/* Wartungsseite Vorschau */
.maintenance-preview {
  margin-top: 3rem;
}

.maintenance-preview h3 {
  margin-bottom: 1rem;
  font-size: 1.25rem;
  color: var(--primary-color);
}

.preview-container {
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.preview-label {
  padding: 0.75rem 1rem;
  background-color: var(--bg-alt);
  border-bottom: 1px solid var(--border-color);
  font-size: 0.9rem;
  color: var(--text-muted);
}

.preview-iframe {
  padding: 2rem;
  min-height: 400px;
  background-color: var(--bg-color);
}

.preview-content {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
}

.preview-content h2 {
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.countdown-preview {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin: 2rem 0;
}

.countdown-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.countdown-number {
  background-color: var(--primary-color);
  color: white;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1.5rem;
  font-weight: 700;
  min-width: 60px;
  text-align: center;
}

.countdown-label {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-muted);
}

.social-preview {
  margin: 2rem 0;
}

.social-icons {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.social-icon-preview {
  background-color: var(--bg-alt);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  color: var(--text-color);
}

.contact-preview {
  margin: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-item {
  font-size: 0.9rem;
}

.contact-label {
  font-weight: 600;
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .form-section {
    padding: 1.5rem;
  }
  
  .form-toggle {
    flex-direction: column;
    gap: 1rem;
  }
  
  .countdown-preview {
    gap: 0.5rem;
  }
  
  .countdown-number {
    font-size: 1.25rem;
    min-width: 50px;
    padding: 0.5rem;
  }
}
</style> 